import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, TablePagination
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchDepartments, updateDepartment, deleteDepartment } from '../services/api';

const DepartmentList = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await fetchDepartments();
      setDepartments(data);
    } catch (err) {
      setError('Failed to load departments');
    }
  };

  const handleOpenDialog = (department) => {
    setEditingDepartment(department);
    setEditName(department.name);
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDepartment(null);
    setEditName('');
    setError('');
  };

  const handleEditSubmit = async () => {
    if (!editName.trim()) {
      setError('Department Name is required');
      return;
    }
    try {
      await updateDepartment(editingDepartment.id, { name: editName });
      handleCloseDialog();
      loadDepartments();
    } catch (err) {
      setError(err.message || 'Failed to update department');
    }
  };

  const handleDelete = async (department) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await deleteDepartment(department.id);
        loadDepartments();
      } catch (err) {
        alert('Failed to delete department');
      }
    }
  };

  const filteredDepartments = departments.filter(d => d.name.toLowerCase().includes(search.toLowerCase()));
  const paginatedDepartments = filteredDepartments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Department List</Typography>
        <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={() => navigate('/departments/add')}>
          Add New Department
        </Button>
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <TextField
              select
              label="Show"
              value={rowsPerPage}
              onChange={e => setRowsPerPage(Number(e.target.value))}
              SelectProps={{ native: true }}
              size="small"
              sx={{ width: 80, mr: 2 }}
            >
              {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </TextField>
            <Typography variant="caption">entries</Typography>
          </Box>
          <TextField
            label="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>NO.</TableCell>
                <TableCell>DEPARTMENT NAME</TableCell>
                <TableCell>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDepartments.map((department, idx) => (
                <TableRow key={department.id}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{department.name}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(department)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(department)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2">
            Showing {filteredDepartments.length === 0 ? 0 : page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredDepartments.length)} of {filteredDepartments.length} entries
          </Typography>
          <TablePagination
            component="div"
            count={filteredDepartments.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage=""
          />
        </Box>
      </Paper>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Department Name"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
          {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentList; 